/**
 * creature/templates/dog.ts — The Loyal Ones
 *
 * Dogs come in many shapes. A Labrador is not a Corgi is not a Husky.
 * But they share a skeleton, a body plan, and one thing cats never will:
 * the desperate, unconditional desire to be near you.
 */

import type { SkeletonDef } from "../skeleton.js";
import type { SpeciesTemplate, GaitPattern } from "../species.js";
import { registerSpecies, createBreed } from "../species.js";

// ─── Dog Skeleton ───────────────────────────────────────────────────────────

const DOG_SKELETON: SkeletonDef = {
  id: "skeleton_dog",
  rootJointId: "hip",
  joints: [
    {
      id: "hip", parentId: null,
      restOffset: { x: 0, y: 0 }, restAngle: -0.4,
      minAngle: -1.4, maxAngle: 0.3, length: 12,
      tags: ["root", "hip"],
    },
    {
      id: "shoulder", parentId: "hip",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.3, maxAngle: 0.3, length: 6,
      tags: ["shoulder", "spine"],
    },
    {
      id: "neck", parentId: "shoulder",
      restOffset: { x: 0, y: 0 }, restAngle: -0.35,
      minAngle: -1.0, maxAngle: 0.4, length: 4,
      tags: ["neck"],
    },
    {
      id: "head", parentId: "neck",
      restOffset: { x: 0, y: 0 }, restAngle: 0,
      minAngle: -0.4, maxAngle: 0.4, length: 0,
      tags: ["head"],
    },
    // Tail (dogs have fewer, thicker tail segments)
    {
      id: "tail_0", parentId: "hip",
      restOffset: { x: 5, y: 0.5 }, restAngle: -0.5,
      minAngle: -1.8, maxAngle: 1.5, length: 6,
      tags: ["tail"],
    },
    {
      id: "tail_1", parentId: "tail_0",
      restOffset: { x: 0, y: 0 }, restAngle: -0.3,
      minAngle: -1.5, maxAngle: 1.5, length: 5,
      tags: ["tail", "tail_tip"],
    },
    // Ears
    {
      id: "ear_l", parentId: "head",
      restOffset: { x: -2.0, y: -1.2 }, restAngle: 0.5,
      minAngle: -0.5, maxAngle: 1.5, length: 3,
      tags: ["ear", "ear_l"],
    },
    {
      id: "ear_r", parentId: "head",
      restOffset: { x: 1.5, y: -1.5 }, restAngle: 0.5,
      minAngle: -0.5, maxAngle: 1.5, length: 3,
      tags: ["ear", "ear_r"],
    },
  ],
  drawOrder: [
    { name: "back_legs",   joints: ["hip"] },
    { name: "tail",        joints: ["hip", "tail_0", "tail_1"] },
    { name: "body",        joints: ["hip", "shoulder"] },
    { name: "front_legs",  joints: ["shoulder"] },
    { name: "head",        joints: ["head", "neck", "ear_l", "ear_r"] },
    { name: "fire_glow",   joints: ["hip", "shoulder"] },
  ],
  ikChains: [
    {
      id: "look_at",
      jointIds: ["shoulder", "neck", "head"],
      targetMode: "point",
      iterations: 3,
      tolerance: 0.5,
    },
  ],
};

// ─── Dog Gaits ──────────────────────────────────────────────────────────────

const DOG_WALK: GaitPattern = {
  name: "walk",
  speedRange: [0, 2.5],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: Math.PI,
    leg_back_l: Math.PI * 0.5, leg_back_r: Math.PI * 1.5,
  },
  strideLength: 0.45,
  bodyBob: 0.35,
  bodyRoll: 0.12,
  headBob: 0.2,
};

const DOG_TROT: GaitPattern = {
  name: "trot",
  speedRange: [2.5, 5],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: Math.PI,
    leg_back_l: Math.PI, leg_back_r: 0,
  },
  strideLength: 0.7,
  bodyBob: 0.5,
  bodyRoll: 0.18,
  headBob: 0.25,
};

const DOG_RUN: GaitPattern = {
  name: "run",
  speedRange: [5, 10],
  legPhaseOffsets: {
    leg_front_l: 0, leg_front_r: 0.3,
    leg_back_l: Math.PI, leg_back_r: Math.PI + 0.3,
  },
  strideLength: 1.2,
  bodyBob: 0.7,
  bodyRoll: 0.1,
  headBob: 0.1,
};

// ─── Base Dog Template ──────────────────────────────────────────────────────

export const SPECIES_DOG: SpeciesTemplate = {
  id: "dog_mixed",
  displayName: "Dog",
  bodyPlan: "quadruped",
  skeleton: DOG_SKELETON,
  size: "medium",
  baseSpeed: 2.5,
  heightAtShoulder: 0.6,
  gaits: [DOG_WALK, DOG_TROT, DOG_RUN],
  turnRate: Math.PI * 1.5,
  palette: {
    body: "#8a6a40",
    markings: "#6a5030",
    eyes: "#503020",
    nose: "#2a1a10",
    accent: "#3a2a1a",
    belly: "#a08060",
  },
  bodyShape: {
    bodyLength: 12,
    bodyWidth: 4.0,
    headRadius: 3.5,
    tailSegments: 2,
    tailLength: 11,
    earStyle: "floppy",
    earSize: 1.0,
    legThickness: 1.8,
    hasMane: false,
    hasWings: false,
    neckLength: 1.0,
    snoutLength: 0.6,
    legLength: 1.1,
  },
  behaviorDefaults: {
    energy: 0.6,
    sociability: 0.85,
    curiosity: 0.6,
    obedience: 0.7,
    playfulness: 0.75,
    courage: 0.5,
    agility: 0.6,
    stamina: 0.7,
    nocturnality: 0.15,
    territoriality: 0.5,
    packInstinct: 0.8,
  },
  rideable: false,
  riderAttachJoint: "",
  riderOffsetZ: 0,
  saddleable: false,
  sounds: {
    idle: ["pant"],
    happy: ["bark_happy", "whine_excited"],
    hungry: ["whine", "bark"],
    walk: ["paw_pad"],
    run: ["paw_rapid"],
    special: ["bark", "bark_deep", "howl_short", "whine", "growl"],
  },
  moodContribution: {
    warmth: 0.05,
    coziness: 0.08,
    energy: 0.06,
    nature: 0.03,
  },
};

// ─── Breed Variants ─────────────────────────────────────────────────────────

export const SPECIES_DOG_LABRADOR = createBreed(SPECIES_DOG, {
  id: "dog_labrador",
  displayName: "Labrador",
  palette: {
    ...SPECIES_DOG.palette,
    body: "#c8a848",
    markings: "#b09040",
    belly: "#d8c070",
  },
  behaviorDefaults: {
    ...SPECIES_DOG.behaviorDefaults,
    sociability: 0.95,
    playfulness: 0.85,
    obedience: 0.8,
    energy: 0.7,
  },
});

export const SPECIES_DOG_HUSKY = createBreed(SPECIES_DOG, {
  id: "dog_husky",
  displayName: "Husky",
  palette: {
    ...SPECIES_DOG.palette,
    body: "#8090a0",
    markings: "#e0e0e8",
    eyes: "#5090d0",
    belly: "#d0d0d8",
  },
  bodyShape: {
    ...SPECIES_DOG.bodyShape,
    bodyWidth: 4.2,
    earStyle: "pointed",
    earSize: 1.1,
    legLength: 1.15,
  },
  behaviorDefaults: {
    ...SPECIES_DOG.behaviorDefaults,
    energy: 0.9,
    stamina: 0.9,
    packInstinct: 0.9,
    obedience: 0.4,
  },
});

export const SPECIES_DOG_CORGI = createBreed(SPECIES_DOG, {
  id: "dog_corgi",
  displayName: "Corgi",
  size: "small",
  palette: {
    ...SPECIES_DOG.palette,
    body: "#d8a030",
    markings: "#f0f0e8",
    belly: "#f0e8d0",
  },
  bodyShape: {
    ...SPECIES_DOG.bodyShape,
    bodyLength: 14,
    bodyWidth: 4.5,
    legLength: 0.55,
    earStyle: "pointed",
    earSize: 1.4,
    tailSegments: 0,
  },
  behaviorDefaults: {
    ...SPECIES_DOG.behaviorDefaults,
    energy: 0.8,
    playfulness: 0.9,
    courage: 0.7,
  },
});

export const SPECIES_DOG_SHEPHERD = createBreed(SPECIES_DOG, {
  id: "dog_shepherd",
  displayName: "German Shepherd",
  size: "large",
  palette: {
    ...SPECIES_DOG.palette,
    body: "#5a3a1a",
    markings: "#c89040",
    belly: "#a07840",
  },
  bodyShape: {
    ...SPECIES_DOG.bodyShape,
    bodyLength: 14,
    earStyle: "pointed",
    earSize: 1.2,
    legLength: 1.2,
  },
  behaviorDefaults: {
    ...SPECIES_DOG.behaviorDefaults,
    obedience: 0.9,
    courage: 0.85,
    territoriality: 0.7,
    energy: 0.75,
  },
});

// ─── Register All Dog Species ───────────────────────────────────────────────

export function registerDogs(): void {
  registerSpecies(SPECIES_DOG);
  registerSpecies(SPECIES_DOG_LABRADOR);
  registerSpecies(SPECIES_DOG_HUSKY);
  registerSpecies(SPECIES_DOG_CORGI);
  registerSpecies(SPECIES_DOG_SHEPHERD);
}
